import React from "react";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import useUser from "../../../../firebase/useUser";
import { useAuth } from "../../../../firebase/useAuth";
import { useLearningModules } from "../../fetchers/learning_modules/useLearningModules";
import { Button, CircularProgress, Collapse, Fade, Grid } from "@mui/material";
import { topicModalStyles } from "../../styles/topicModalStyles";
import { TransitionProps } from "@mui/material/transitions";
import Slide from "@mui/material/Slide";
import Image from "next/image";
import clsx from "clsx";
import { useState } from "react";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import useUserLearningModule from "../../fetchers/useUserLearningModule";
import usePrepFqProfile from "../../fetchers/usePrepFqProfile";

interface OwnProps {
  open: boolean;
  setOpen(open: boolean): any;
}
type Props = OwnProps;

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...(props as any)} />;
});

const BcgModal: React.FC<Props> = ({ open, setOpen }) => {
  const { t } = useTranslation(["common", "dashboard"]);
  const classes = topicModalStyles();
  const [warningOpen, setWarningOpen] = useState(false);
  const authUser = useAuth();
  const { user } = useUser(authUser?.user?.uid);
  const { isLoading: prepFqProfileLoading } = usePrepFqProfile(
    user?.primary_profile_id
  );
  const { learningModules } = useLearningModules();
  const learningModule = learningModules.find(
    (learning_module) => learning_module.module_id === "behaviour_change_guide"
  );
  const appendixModule = learningModules.find(
    (learning_module) =>
      learning_module.module_id === "behaviour_change_guide_appendix"
  );
  const { isLoading: bcgModuleLoading } = useUserLearningModule(
    user?.user_id,
    "behaviour_change_guide"
  );
  const { isLoading: bcgaModuleLoading } = useUserLearningModule(
    user?.user_id,
    "behaviour_change_guide_appendix"
  );
  const isLoading =
    prepFqProfileLoading || bcgModuleLoading || bcgaModuleLoading;
  const handleClose = () => {
    setOpen(false);
  };
  const handleWarningClose = () => {
    setWarningOpen(false);
  };
  const handleOpen = () => {
    setWarningOpen(true);
  };
  const getButtonLink = (stage_of_change: string) => {
    if (appendixModule) {
      const pageKeys = Object.keys(appendixModule.pages);
      const keyToUse = pageKeys.find((key) =>
        appendixModule.pages[key]
          .toLowerCase()
          .includes(stage_of_change.toLowerCase())
      );
      if (keyToUse) {
        return `${appendixModule.module_link}#/lessons/${keyToUse}`;
      } else {
        return "";
      }
    } else {
      return "";
    }
  };
  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        keepMounted={true}
        fullWidth={true}
        maxWidth="md"
        TransitionComponent={Transition}
        classes={{
          paper: classes.root,
        }}
        scroll={"body"}
      >
        <DialogContent
          classes={{
            root: classes.content,
          }}
          className={"eleventh-step-topic-landing"}
          id="seventeenth-step-topic-close__observe"
        >
          <IconButton
            aria-label="close"
            className={clsx(
              classes.closeButton,
              "seventeenth-step-topic-close"
            )}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <Collapse timeout={0} in={isLoading}>
            <Fade in={isLoading}>
              <div style={{ minHeight: 700 }} className={classes.centered}>
                <CircularProgress style={{ color: "white", fontSize: 80 }} />
              </div>
            </Fade>
          </Collapse>
          <Collapse timeout={0} in={!isLoading}>
            <Fade mountOnEnter unmountOnExit in={!isLoading}>
              <div>
                {/* Title */}
                <Grid container>
                  <Grid className={classes.centered} md={2} sm={4} xs={12} item>
                    <div
                      className={clsx(classes.iconContainer, classes.centered)}
                    >
                      <Image
                        alt="Behaviour Change Guide"
                        src={"/bcg-leaf.svg"}
                        layout={"intrinsic"}
                        width={72}
                        height={72}
                      />
                    </div>
                  </Grid>
                  <Grid
                    className={clsx(classes.leftCentered, classes.column)}
                    md={10}
                    sm={8}
                    xs={12}
                    item
                  >
                    <h3 className={classes.title}>
                      {learningModule?.module_name}
                    </h3>
                    <Grid container>
                      <Grid xs={12} item>
                        <p
                          className={clsx(
                            classes.mortality,
                            classes.centeredTextMobile
                          )}
                          style={{ fontWeight: "normal" }}
                        >
                          {t("prepFq:report.bcgModal.description")}
                        </p>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <div className={classes.divider} />
                <Grid container>
                  <Grid
                    className={classes.centered}
                    md={4}
                    sm={12}
                    xs={12}
                    item
                  >
                    <div
                      className={clsx(
                        classes.bcgGridItem,
                        classes.bcgGridItemFirst
                      )}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        size="large"
                        className={classes.bcgButton}
                        href={`https://behaviour.planwellguide.com?email=${user?.email}`}
                      >
                        {t("prepFq:report.bcgModal.open.title")}
                      </Button>
                      <div>
                        <p
                          className={classes.bcgText}
                          style={{
                            color: "#515475",
                            fontStyle: "italic",
                          }}
                        >
                          {t("prepFq:report.bcgModal.open.subTitle")}
                        </p>
                        <p
                          className={classes.bcgText}
                          style={{ color: "#515475", marginTop: 4 }}
                        >
                          {t("prepFq:report.bcgModal.open.description")}
                        </p>
                      </div>
                    </div>
                  </Grid>
                  <Grid
                    className={classes.centered}
                    md={4}
                    sm={12}
                    xs={12}
                    item
                  >
                    <div className={classes.bcgGridItem}>
                      <Button
                        variant="contained"
                        color="secondary"
                        href={getButtonLink(
                          "Stage of Change: Pre-Contemplative"
                        )}
                        rel="noreferrer noopener"
                        fullWidth
                        size="large"
                        className={classes.bcgButton}
                      >
                        {t("prepFq:report.bcgModal.pre_contemplative.title")}
                      </Button>
                      <p className={classes.bcgText}>
                        {t(
                          "prepFq:report.bcgModal.pre_contemplative.description"
                        )}
                      </p>
                    </div>
                  </Grid>
                  <Grid
                    className={classes.centered}
                    md={4}
                    sm={12}
                    xs={12}
                    item
                  >
                    <div className={classes.bcgGridItem}>
                      <Button
                        variant="contained"
                        color="secondary"
                        href={getButtonLink("Stage of Change: Contemplative")}
                        rel="noreferrer noopener"
                        fullWidth
                        size="large"
                        className={classes.bcgButton}
                      >
                        {t("prepFq:report.bcgModal.contemplative.title")}
                      </Button>
                      <p className={classes.bcgText}>
                        {t("prepFq:report.bcgModal.contemplative.description")}
                      </p>
                    </div>
                  </Grid>
                </Grid>
                <div className={classes.divider} />
                <Grid container>
                  <Grid
                    className={classes.centered}
                    md={4}
                    sm={12}
                    xs={12}
                    item
                  >
                    <div className={classes.bcgGridItem}>
                      <Button
                        variant="contained"
                        color="secondary"
                        href={getButtonLink("Stage of Change: Preparation")}
                        rel="noreferrer noopener"
                        fullWidth
                        size="large"
                        className={classes.bcgButton}
                      >
                        {t("prepFq:report.bcgModal.preparation.title")}
                      </Button>
                      <p className={classes.bcgText}>
                        {t("prepFq:report.bcgModal.preparation.description")}
                      </p>
                    </div>
                  </Grid>
                  <Grid
                    className={classes.centered}
                    md={4}
                    sm={12}
                    xs={12}
                    item
                  >
                    <div className={classes.bcgGridItem}>
                      <Button
                        variant="contained"
                        color="secondary"
                        href={getButtonLink("Stage of Change: Action")}
                        rel="noreferrer noopener"
                        fullWidth
                        size="large"
                        className={classes.bcgButton}
                      >
                        {t("prepFq:report.bcgModal.action.title")}
                      </Button>
                      <p className={classes.bcgText}>
                        {t("prepFq:report.bcgModal.action.description")}
                      </p>
                    </div>
                  </Grid>
                  <Grid
                    className={classes.centered}
                    md={4}
                    sm={12}
                    xs={12}
                    item
                  >
                    <div className={classes.bcgGridItem}>
                      <Button
                        variant="contained"
                        color="secondary"
                        href={getButtonLink("Stage of Change: Maintenance")}
                        rel="noreferrer noopener"
                        fullWidth
                        size="large"
                        className={classes.bcgButton}
                      >
                        {t("prepFq:report.bcgModal.maintenance.title")}
                      </Button>
                      <p className={classes.bcgText}>
                        {t("prepFq:report.bcgModal.maintenance.description")}
                      </p>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Fade>
          </Collapse>
        </DialogContent>
      </Dialog>
      <Dialog
        open={warningOpen}
        onClose={handleWarningClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" style={{ fontSize: 24 }}>
          {t("prepFq:topicModal.unavailableModal.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            style={{ fontSize: 18 }}
          >
            {t("prepFq:topicModal.unavailableModal.description")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleWarningClose}
            color="secondary"
            variant="contained"
          >
            {t("prepFq:topicModal.unavailableModal.button")}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default BcgModal;
